/**
 * Zet een video-URL om naar een iframe-embed-URL waar mogelijk.
 * Ondersteunt: YouTube, Vimeo, Vidyard (share/watch/play links).
 * Voor geüploade bestanden (/uploads/...) of onbekende URL's: null (gebruik <video> of link).
 */
export function resolveVideoEmbedUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  const isYouTube = /youtube\.com|youtu\.be/i.test(trimmed);
  const isVimeo = /vimeo\.com/i.test(trimmed);
  const isVidyard =
    /vidyard\.com/i.test(trimmed) ||
    /share\.vidyard/i.test(trimmed) ||
    /hubs\.vidyard/i.test(trimmed);

  if (isYouTube) {
    return trimmed
      .replace('watch?v=', 'embed/')
      .replace('youtu.be/', 'youtube.com/embed/');
  }

  if (isVimeo) {
    return trimmed.replace('vimeo.com/', 'player.vimeo.com/video/');
  }

  if (isVidyard) {
    // Al embed-klaar: https://play.vidyard.com/{uuid}
    const playMatch = trimmed.match(/play\.vidyard\.com\/([a-zA-Z0-9_-]+)/i);
    if (playMatch) {
      return `https://play.vidyard.com/${playMatch[1]}`;
    }
    // Share / watch: .../watch/{uuid} of hubs
    const watchMatch = trimmed.match(/\/watch\/([a-zA-Z0-9_-]+)/i);
    if (watchMatch) {
      return `https://play.vidyard.com/${watchMatch[1]}`;
    }
    // Soms alleen pad met UUID als laatste segment
    try {
      const parsed = new URL(trimmed);
      const segments = parsed.pathname.split('/').filter(Boolean);
      const last = segments[segments.length - 1];
      if (last && /^[a-zA-Z0-9_-]{8,}$/.test(last)) {
        return `https://play.vidyard.com/${last}`;
      }
    } catch {
      /* invalid URL */
    }
    return null;
  }

  return null;
}

/** True als de URL als iframe-embed kan (YouTube / Vimeo / Vidyard). */
export function isEmbeddableVideoUrl(url: string): boolean {
  return resolveVideoEmbedUrl(url) !== null;
}

/** Voegt autoplay toe aan embed-URL (iframe). */
export function embedUrlWithAutoplay(embedUrl: string): string {
  if (!embedUrl) return embedUrl;
  try {
    const u = new URL(embedUrl);
    if (!u.searchParams.has('autoplay')) {
      u.searchParams.set('autoplay', '1');
    }
    return u.toString();
  } catch {
    return embedUrl.includes('?') ? `${embedUrl}&autoplay=1` : `${embedUrl}?autoplay=1`;
  }
}

/** Geüploade video in onze uploads-map */
export function isUploadedVideoUrl(url: string): boolean {
  return url.trim().startsWith('/uploads/');
}
